// src/pages/EnviarMensagensClientes.jsx
import React, { useMemo, useRef, useState } from "react";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import { showError, showInfo } from "../utils/toast";
import "../sass/pages/sendMessage.scss";

/**
 * Rotas utilizadas:
 *  - GET  /ssw/cliente/por-codigo?codigo=123
 *  - GET  /ssw/cliente/filtro?termo=ACME
 *  - GET  /ssw/cliente/por-sistema?sistema=SPW|web|local&ativos=1&page=1&limit=1000
 *  - POST /ssw/mensagens
 */

// —— Serviços ————————————————————————————————————————————————————————————————
async function apiBuscarPorCodigo(codigo) {
  const res = await api.get("/cliente/por-codigo", { params: { codigo } });
  const c = res.data;
  return { data: c ? [c] : [], total: c ? 1 : 0 };
}

async function apiBuscarPorRazao(termo) {
  const res = await api.get("/cliente/filtro", { params: { termo } });
  const arr = Array.isArray(res.data) ? res.data : [];
  return { data: arr, total: arr.length };
}

async function apiBuscarPorSistema(sistema, page, limit, apenasAtivos) {
  const res = await api.get("/cliente/por-sistema", {
    params: { sistema, page, limit, ativos: apenasAtivos ? 1 : 0 },
  });
  const payload = res.data;

  if (Array.isArray(payload)) return { data: payload, total: payload.length };
  if (payload?.data)
    return { data: payload.data, total: payload.total ?? payload.data.length };

  return { data: payload ? [payload] : [], total: payload ? 1 : 0 };
}

async function apiEnviarMensagens({
  clienteIds,
  mensagem,
  validadeData,
  validadeHora,
  cor,
}) {
  const payload = {
    clientes: clienteIds,
    mensagem,
    validadeData,
    validadeHora,
    cor,
  };
  const res = await api.post("/mensagens", payload);
  return res.data;
}

// —— Helpers ————————————————————————————————————————————————————————————————
const cores = [
  { value: "1", label: "1 - Amarelo", className: "is-yellow" },
  { value: "2", label: "2 - Verde", className: "is-green" },
  { value: "3", label: "3 - Azul", className: "is-blue" },
  { value: "4", label: "4 - Branco", className: "is-white" },
  { value: "5", label: "5 - Vermelho", className: "is-red" },
];

// Catálogo fixo
const TIPOS_STATIC = [
  { id: "local", label: "Sistema local", value: "local" },
  { id: "web", label: "Servidor web", value: "web" },
  { id: "SPW", label: "Spw", value: "SPW" },
];

function hojeISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
}
function agoraHM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
}
function toBrDate(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  return `${d}/${m}/${y}`;
}

/**
 * Inferência de tipo de sistema ALINHADA ao banco:
 *  - ServidorWeb = 'LOCAL'  -> local
 *  - ServidorWeb = 'WEB'    -> web
 *  - ServidorWeb = 'SPW'    -> SPW
 * Mantém fallback em flags antigas (SPW / IsWeb) se existirem.
 */
function inferTipoSistema(c) {
  const servidor = String(
    c.ServidorWeb ?? c.SistemaWeb ?? ""
  )
    .trim()
    .toUpperCase();

  if (servidor === "SPW") return "SPW";
  if (servidor === "WEB") return "web";
  if (servidor === "LOCAL") return "local";

  // Fallback legado
  const spwFlag = c.spw ?? c.SPW ?? c.SpW;
  if (
    String(spwFlag).toUpperCase() === "S" ||
    spwFlag === 1 ||
    spwFlag === true
  )
    return "SPW";

  const webFlag = c.IsWeb ?? c.isWeb ?? c.Web;
  if (
    String(webFlag).toUpperCase() === "S" ||
    webFlag === 1 ||
    webFlag === true
  )
    return "web";

  return "local";
}

/**
 * Ativo alinhado com query usada no SQL:
 *  Desativado = 'False' AND TipCad = 'Cliente de Software'
 */
function isAtivo(c) {
  const desativado =
    String(c.Desativado ?? "").trim().toLowerCase() === "true";

  const tip = String(c.TipCad ?? "")
    .trim()
    .toLowerCase();

  const ehClienteSoftware = tip === "cliente de software";

  return !desativado && ehClienteSoftware;
}

// Normalização
function normalize(list) {
  return (list || []).map((c) => {
    const _id = c.CodigoCad ?? c.ID ?? c.Id ?? c.id;
    return { ...c, _id, _tipo: inferTipoSistema(c), _ativo: isAtivo(c) };
  });
}

// —— Primitives (SCSS) ———————————————————————————————————————————————
const Button = ({ children, variant = "primary", className = "", ...props }) => (
  <button className={`ssw-btn ssw-btn--${variant} ${className}`} {...props}>
    {children}
  </button>
);
const Input = ({ className = "", ...props }) => (
  <input {...props} className={`ssw-input ${className}`} />
);
const Select = ({ className = "", ...props }) => (
  <select {...props} className={`ssw-select ${className}`} />
);
const Textarea = ({ className = "", ...props }) => (
  <textarea {...props} className={`ssw-textarea ${className}`} />
);

export default function EnviarMensagensClientes() {
  // filtros
  const [modo, setModo] = useState("codigo"); // "codigo" | "razao" | "tipo"
  const [texto, setTexto] = useState("");
  const [tipo, setTipo] = useState("");
  const [somenteAtivos, setSomenteAtivos] = useState(true);

  // validade + mensagem
  const [dtVal, setDtVal] = useState(hojeISO());
  const [hrVal, setHrVal] = useState(agoraHM());
  const [cor, setCor] = useState("1");
  const [mensagem, setMensagem] = useState("");

  // dataset + paginação client-side
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const total = raw.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pageData = useMemo(() => {
    const start = (page - 1) * limit;
    return raw.slice(start, start + limit);
  }, [raw, page, limit]);

  // seleção (global na busca, não só na página)
  const [checked, setChecked] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // envio / progresso
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);

  // —— Ações ————————————————————————————————————————————————————————————————
  async function handleBuscar() {
    if (modo === "codigo" && !texto.trim())
      return showError("Informe o código.");
    if (modo === "razao" && !texto.trim())
      return showError("Informe a Razão Social.");
    if (modo === "tipo" && !tipo)
      return showError("Selecione o Tipo de Sistema.");

    setLoading(true);
    setChecked(new Set());
    setSelectAll(false);
    setPage(1);

    try {
      let r;
      if (modo === "codigo") {
        r = await apiBuscarPorCodigo(texto.trim());
      } else if (modo === "razao") {
        r = await apiBuscarPorRazao(texto.trim());
      } else {
        // por tipo – back já filtra por sistema e, se informado, por ativos
        r = await apiBuscarPorSistema(tipo, 1, 1000, somenteAtivos);
      }

      // normaliza e reforça filtros também no front
      const norm = normalize(r.data).filter((c) => {
        const tipoOk = modo !== "tipo" ? true : c._tipo === tipo;
        const ativoOk = !somenteAtivos ? true : c._ativo === true;
        return tipoOk && ativoOk;
      });

      setRaw(norm);
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.message || "Falha na consulta de clientes.";
      showError(msg);
      setRaw([]);
    } finally {
      setLoading(false);
    }
  }

  function toggleRow(id) {
    const next = new Set(checked);
    next.has(id) ? next.delete(id) : next.add(id);
    setChecked(next);
    // se tirou algum, desmarca o “selectAll” visual
    if (next.size !== raw.length) setSelectAll(false);
  }

  // “Selecionar todos” GLOBAL da busca (todas as páginas)
  function toggleSelectAll() {
    if (selectAll) {
      setChecked(new Set());
      setSelectAll(false);
      return;
    }
    const next = new Set(raw.map((c) => c._id));
    setChecked(next);
    setSelectAll(true);
  }

  function validarEnvio() {
    if (!mensagem?.trim()) return "Mensagem obrigatória";
    if (!dtVal) return "Data de validade obrigatória";
    if (!hrVal) return "Hora de validade obrigatória";

    const hoje = new Date();
    const dataVal = new Date(`${dtVal}T00:00:00`);
    if (
      dataVal <
      new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())
    )
      return "A data deve ser hoje ou futura";
    if (!cor) return "Selecione a cor do aviso";
    if (checked.size === 0) return "Selecione pelo menos um cliente";
    return null;
  }

  async function handleEnviar() {
    const erro = validarEnvio();
    if (erro) return showError(erro);

    const ids = Array.from(checked);
    setSending(true);
    setProgress(0);

    try {
      let pct = 0;
      const t = setInterval(() => {
        pct = Math.min(95, pct + 3);
        setProgress(pct);
      }, 200);

      await apiEnviarMensagens({
        clienteIds: ids,
        mensagem: mensagem.trim(),
        validadeData: toBrDate(dtVal),
        validadeHora: hrVal,
        cor,
      });

      clearInterval(t);
      setProgress(100);
      showInfo("Mensagens processadas com sucesso.");
      setChecked(new Set());
      setSelectAll(false);
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.message || "Falha no envio.";
      showError(msg);
    } finally {
      setSending(false);
      setTimeout(() => setProgress(0), 800);
    }
  }

  const msgRef = useRef(null);
  function onKeyDownMsg(e) {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") handleEnviar();
  }

  const corClass = cores.find((c) => c.value === cor)?.className || "";

  // —— Render ————————————————————————————————————————————————————————————————
  return (
    <MainLayout>
      <div className="ssw-container">
        {/* Header */}
        <div className="ssw-header">
          <div>
            <h1>Enviar mensagens</h1>
          </div>
        </div>

        {/* Filtros */}
        <div className="ssw-card ssw-filters">
          <div className="ssw-field">
            <label>Forma de pesquisa</label>
            <Select
              value={modo}
              onChange={(e) => {
                setModo(e.target.value);
                setTexto("");
                setTipo("");
                setRaw([]);
                setPage(1);
                setChecked(new Set());
                setSelectAll(false);
              }}
            >
              <option value="codigo">Código de Cadastro</option>
              <option value="razao">Razão Social</option>
              <option value="tipo">Tipo de Sistema</option>
            </Select>
          </div>

          {(modo === "codigo" || modo === "razao") && (
            <div className="ssw-field">
              <label>{modo === "codigo" ? "Código" : "Razão Social"}</label>
              <Input
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder={
                  modo === "codigo"
                    ? "Ex.: 12345"
                    : "Digite a razão social"
                }
                onKeyDown={(e) => e.key === "Enter" && handleBuscar()}
              />
            </div>
          )}

          {modo === "tipo" && (
            <>
              <div className="ssw-field">
                <label>Tipo de Sistema</label>
                <Select
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                >
                  <option value="">Selecione…</option>
                  {TIPOS_STATIC.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="ssw-field ssw-checkbox">
                <label className="ssw-checkbox__label">
                  <input
                    type="checkbox"
                    checked={somenteAtivos}
                    onChange={(e) => setSomenteAtivos(e.target.checked)}
                  />
                </label>
                <span>Somente clientes ativos</span>
              </div>
            </>
          )}

          <div className="ssw-field">
            <label>Dt validade</label>
            <Input
              type="date"
              value={dtVal}
              min={hojeISO()}
              onChange={(e) => setDtVal(e.target.value)}
            />
          </div>

          <div className="ssw-field">
            <label>Hr validade</label>
            <Input
              type="time"
              value={hrVal}
              onChange={(e) => setHrVal(e.target.value)}
            />
          </div>

          <div className="ssw-field ssw-color-picker">
            <label>Cor do aviso</label>
            <div className="ssw-color-row">
              <Select value={cor} onChange={(e) => setCor(e.target.value)}>
                {cores.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
              <span className={`ssw-swatch ${corClass}`} />
            </div>
          </div>

          <div className="ssw-field ssw-actions-row">
            <Button onClick={handleBuscar} disabled={loading}>
              {loading ? "Pesquisando…" : "Pesquisar"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setTexto("");
                setTipo("");
                setRaw([]);
                setPage(1);
                setLimit(10);
                setChecked(new Set());
                setSelectAll(false);
              }}
            >
              Limpar
            </Button>
          </div>
        </div>

        {/* Grid */}
        <div className="ssw-card">
          <div className="ssw-table-toolbar">
            <div className="ssw-checkall">
              <input
                type="checkbox"
                checked={selectAll && total > 0 && checked.size === total}
                onChange={toggleSelectAll}
                aria-label="Selecionar todos"
              />
              <span>Selecionar todos ({checked.size})</span>
            </div>
            <div className="ssw-table-ops">
              <span className="ssw-badge">{total} resultados</span>
              <Select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 25, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}/página
                  </option>
                ))}
              </Select>
            </div>
          </div>

          <div className="ssw-tablewrap">
            <table className="ssw-table">
              <thead>
                <tr>
                  <th className="w-12"></th>
                  <th>Razão Social</th>
                  <th>Telefone 1</th>
                  <th>Cód. Cad.</th>
                  <th>ID</th>
                  <th>Tipo</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageData.length === 0 && (
                  <tr>
                    <td colSpan={7} className="ssw-empty">
                      {loading ? "Carregando…" : "Nenhum registro"}
                    </td>
                  </tr>
                )}
                {pageData.map((c) => {
                  const id = c._id;
                  const marcado = checked.has(id);
                  return (
                    <tr key={id} className={marcado ? "is-selected" : ""}>
                      <td>
                        <input
                          type="checkbox"
                          checked={marcado}
                          onChange={() => toggleRow(id)}
                          aria-label={`Selecionar ${c.RazaoSocial}`}
                        />
                      </td>
                      <td onClick={() => toggleRow(id)}>{c.RazaoSocial}</td>
                      <td>{c.Tel1 || "—"}</td>
                      <td>{c.CodigoCad || "—"}</td>
                      <td>{id}</td>
                      <td>{c._tipo?.toUpperCase?.() || "—"}</td>
                      <td>{c._ativo ? "ATIVO" : "INATIVO"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="ssw-pagination">
            <div>
              Página {page} de {totalPages}
            </div>
            <div className="ssw-pager">
              <Button
                variant="ghost"
                onClick={() => setPage(1)}
                disabled={page === 1}
              >
                «
              </Button>
              <Button
                variant="ghost"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Anterior
              </Button>
              <Button
                variant="ghost"
                onClick={() =>
                  setPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={page >= totalPages}
              >
                Próxima
              </Button>
              <Button
                variant="ghost"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
              >
                »
              </Button>
            </div>
          </div>
        </div>

        {/* Redator + Ações */}
        <div className="ssw-compose">
          <div className="ssw-compose__editor">
            <label>Mensagem a ser enviada</label>
            <Textarea
              ref={msgRef}
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              onKeyDown={onKeyDownMsg}
              placeholder="Escreva sua mensagem. Use Ctrl+Enter para enviar."
            />
            <div className="ssw-meta">Selecionados: {checked.size}</div>
          </div>
          <div className="ssw-compose__aside">
            <Button onClick={handleEnviar} disabled={sending}>
              {sending ? "Processando…" : "Gravar/Enviar"}
            </Button>
            {(sending || progress > 0) && (
              <div className="ssw-progress" style={{ "--pct": `${progress}%` }}>
                <div className="ssw-progress__bar" />
              </div>
            )}
            <div className="ssw-preview">
              <div className="ssw-preview__title">Prévia</div>
              <div className="ssw-preview__row">
                <span className={`ssw-swatch ${corClass}`} />
                <span>{cores.find((c) => c.value === cor)?.label}</span>
              </div>
              <div className="ssw-preview__body">
                {mensagem || "—"}
              </div>
              <div className="ssw-preview__foot">
                Válido até {toBrDate(dtVal)} {hrVal}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
